import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { AcmMetricKey, AcmRecord } from '../types/kpi.js';
import {
  CHART_COLORS,
  CHART_COLORS_EXTENDED,
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
} from '../lib/chart-theme.js';
import { formatPercent } from './cs-inquiry/CsInquiryRateChart.js';

/**
 * CVR（%）。セッション数が0の期間は率が定義できないため null を返し、線を途切れさせる。
 * @graph-connects none
 */
export function computeCvr(subscriptionNew: number, sessionCount: number): number | null {
  if (sessionCount <= 0) return null;
  return (subscriptionNew / sessionCount) * 100;
}

/** @graph-connects none */
export function formatCvr(v: number | string | null): string {
  return v === null ? '-' : formatPercent(v);
}

/**
 * CVR チャートの系列。分子・分母は粒度集約後の値（期間合計）を割る。
 * 全体は本番DBの新規サブスク登録数、自然検索・SEM は GA4 の登録完了イベント数が分子。
 * 濃紺（CHART_COLORS[0]）はダークテーマで背景に沈むため使わない。
 * @graph-connects none
 */
const CVR_SERIES = [
  {
    key: 'cvr',
    name: '全体',
    numerator: 'subscriptionNew',
    denominator: 'sessionCount',
    color: CHART_COLORS[1],
  },
  {
    key: 'organicCvr',
    name: '自然検索（GA4）',
    numerator: 'organicGa4SubscribeComplete',
    denominator: 'organicGa4Sessions',
    color: CHART_COLORS[3],
  },
  {
    key: 'semCvr',
    name: 'SEM（GA4）',
    numerator: 'semGa4SubscribeComplete',
    denominator: 'semGa4Sessions',
    color: CHART_COLORS_EXTENDED[5],
  },
] as const satisfies readonly {
  key: string;
  name: string;
  numerator: AcmMetricKey;
  denominator: AcmMetricKey;
  color: string;
}[];

interface KpiCvrChartProps {
  aggregated: { label: string; record: AcmRecord }[];
}

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次KPIのCVR推移チャート。全体（新規サブスク登録数÷セッション数）と、GA4 の登録完了数÷同じ流入元のセッション数で求めた自然検索・SEM の CVR を折れ線グラフで並べる。週次以上は日次CVRの平均ではなく期間合計どうしの比。
 * @graph-connects computeCvr [calls] CVR算出
 */
export function KpiCvrChart({ aggregated }: KpiCvrChartProps) {
  const chartData = aggregated.map((a) => {
    const point: Record<string, string | number | null> = { label: a.label };
    for (const series of CVR_SERIES) {
      point[series.key] = computeCvr(a.record[series.numerator], a.record[series.denominator]);
    }
    return point;
  });

  if (chartData.every((d) => CVR_SERIES.every((series) => d[series.key] === null))) return null;

  return (
    <Card className="p-4">
      <h3 className="mb-1 text-sm font-semibold text-[var(--color-text-primary)]">CVR</h3>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        全体は新規サブスク登録数（本番DB）÷ セッション数、自然検索・SEM は GA4 の登録完了イベント数
        ÷ 同じ流入元のセッション数。期間合計どうしの比
      </p>
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
            <YAxis
              tick={AXIS_TICK_STYLE}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatPercent}
            />
            <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={formatCvr as never} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {CVR_SERIES.map((series) => (
              <Line
                key={series.key}
                type="monotone"
                dataKey={series.key}
                name={series.name}
                stroke={series.color}
                dot={false}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
